import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SpreadsheetTable } from './SpreadsheetTable';
import { RefundSectionButton } from './RefundSectionButton';
import { RefundMenu } from './RefundMenu';
import { RefundItemCard } from './RefundItemCard';
import { ArrowRight, LucideIcon } from 'lucide-react';

interface Column {
  header: string;
  accessor: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  editable?: boolean;
  columnType?: 'text' | 'number' | 'select' | 'date' | 'select-with-other' | 'country';
  options?: string[];
  optionsWithIcons?: Array<{ value: string; icon: any }>;
  primaryField?: boolean;
  showTooltip?: boolean;
  tooltipAccessor?: string;
}

interface RefundReportSectionProps {
  title: string;
  description: string;
  icon?: LucideIcon;
  buttonText?: string;
  onButtonClick?: () => void;
  columns: Column[];
  data: any[];
  onCellEdit?: (rowIndex: number, columnKey: string, newValue: any) => void;
  onRetryConversion?: (rowId: string) => Promise<void>;
  onRefundAll?: () => void;
  onRefundCapped?: (maxAmount: number) => void;
  onRefundPartial?: (percentage: number) => void;
  onRefundZero?: () => void;
}

export const RefundReportSection: React.FC<RefundReportSectionProps> = ({
  title,
  description,
  icon: Icon,
  buttonText,
  onButtonClick,
  columns,
  data,
  onCellEdit,
  onRetryConversion,
  onRefundAll,
  onRefundCapped,
  onRefundPartial,
  onRefundZero
}) => {
  const hasData = data.length > 0;
  const showRefundMenu = hasData && onRefundAll && onRefundCapped && onRefundPartial;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2 flex-1">
            <CardTitle className="flex items-center gap-2">
              {Icon && <Icon className="h-5 w-5" />}
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {showRefundMenu && (
              <RefundMenu
                onRefundAll={onRefundAll}
                onRefundCapped={onRefundCapped}
                onRefundPartial={onRefundPartial}
                onRefundZero={onRefundZero}
              />
            )}
            {buttonText && onButtonClick && (
              <>
                {hasData ? (
                  <RefundSectionButton onClick={onButtonClick} className="flex-1 sm:flex-none">
                    {buttonText}
                  </RefundSectionButton>
                ) : (
                  <Button
                    size="lg"
                    onClick={onButtonClick}
                    className="flex items-center justify-center gap-2 w-full sm:min-w-[280px] text-base sm:text-lg h-12 sm:h-14"
                  >
                    {buttonText}
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <>
            {/* Desktop view - Table */}
            <div className="hidden sm:block">
              <SpreadsheetTable
                columns={columns}
                data={data}
                onCellEdit={onCellEdit}
                onRetryConversion={onRetryConversion}
              />
            </div>

            {/* Mobile view - Cards */}
            <div className="sm:hidden space-y-3">
              {data.map((item, index) => (
                <RefundItemCard
                  key={index}
                  item={item}
                  columns={columns}
                  index={index}
                />
              ))}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
